import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { settingsAPI, handleAPIError } from '../services/api';
import SEOHead from '../components/SEOHead';
import {
  Mail,
  Key,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const AccountRestore = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [restored, setRestored] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    restoration_code: ''
  });

  // Prefill from URL params if present (?email=...&code=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get('email') || '';
    const code = params.get('code') || params.get('restoration_code') || '';
    if (email || code) {
      setFormData(prev => ({
        email: email || prev.email,
        restoration_code: code || prev.restoration_code,
      }));
    }
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRestore = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.restoration_code) {
      toast({
        title: "Missing Information",
        description: "Please fill in both email and restoration code",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await settingsAPI.restoreAccount({
        email: formData.email.trim(),
        restoration_code: formData.restoration_code.trim()
      });
      
      setRestored(true);
      
      toast({
        title: "Account Restored! 🎉",
        description: "Your account has been successfully restored. You can now log in.",
      });
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Account restoration error:', error);
      const errorMessage = handleAPIError(error, 'Failed to restore account');
      toast({
        title: "Restoration Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (restored) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <SEOHead 
          title="Account Restored - BrixBot"
          description="Account restoration successful"
          isIndexable={false}
        />
        <Card className="w-full max-w-md bg-gray-900 border-gray-700">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">Account Restored!</CardTitle>
            <CardDescription>
              Your BrixBot account has been successfully restored
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <p className="text-gray-300 mb-4">
                Welcome back! You can now log in to your account and access all your chatbots.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-violet-600 hover:bg-violet-700"
              >
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <SEOHead 
        title="Account Restore - BrixBot"
        description="Restore your deleted BrixBot account"
        isIndexable={false}
      />
      <Card className="w-full max-w-md bg-gray-900 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white p-0"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
          <CardTitle className="text-2xl text-white text-center">Restore Your Account</CardTitle>
          <CardDescription className="text-center">
            Enter your email and restoration code to restore your BrixBot account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRestore} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className="pl-10 bg-gray-800 border-gray-600 text-white"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="restoration_code" className="text-gray-300">Restoration Code</Label>
              <div className="relative">
                <Key className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  id="restoration_code"
                  name="restoration_code"
                  type="text"
                  value={formData.restoration_code}
                  onChange={handleInputChange}
                  placeholder="Enter 6-digit restoration code"
                  className="pl-10 bg-gray-800 border-gray-600 text-white"
                  maxLength={6}
                  required
                />
              </div>
            </div>

            <div className="p-4 bg-yellow-950/20 border border-yellow-600 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-yellow-200 text-sm">
                    <strong>Need help?</strong> Check your email for the restoration code sent when you deleted your account.
                    The code is valid for 30 days from the deletion request.
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-violet-600 hover:bg-violet-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring Account...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Restore Account
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountRestore;